import type { EscalationLevel } from '@badger-sentinel/rules';

import { ESCALATION_TICKS, meterPct, type EscalationProjection } from '../model/escalation.js';

/**
 * The escalation meter (design 118–133).
 *
 * Counter, band, band ticks, and — where the seat has queued orders — the
 * projection they carry. Both the counter and the level are public to both
 * teams (RB §7.1), so nothing on this bar is a fog-of-war question.
 *
 * The projection is drawn as a **hatched span**, never as a second reading. A
 * solid extension would claim the counter is going there; the hatch says the
 * range is where it may land, and the label's `≥` or `n–m` says the same thing
 * in words for anyone who cannot see the texture.
 */

export interface EscalationMeterProps {
  readonly counter: number;
  readonly level: EscalationLevel;
  readonly projection: EscalationProjection | null;
  readonly width?: number;
}

export function EscalationMeter(props: EscalationMeterProps): JSX.Element {
  const width = props.width ?? 132;
  const fillPct = meterPct(props.counter);
  const projectedPct =
    props.projection === null ? 0 : meterPct(props.counter + props.projection.maxAdd) - fillPct;

  return (
    <div data-testid="escalation" style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <span style={LABEL}>ESCALATION</span>
      <span
        data-testid="escalation-counter"
        style={{
          fontFamily: 'var(--font-data)',
          fontSize: 14,
          fontWeight: 600,
          lineHeight: 1,
          color: 'var(--text)',
        }}
      >
        {String(props.counter).padStart(2, '0')}
      </span>
      {props.projection !== null && (
        <span
          data-testid="escalation-projection"
          style={{
            fontFamily: 'var(--font-data)',
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1,
            whiteSpace: 'nowrap',
            color: 'var(--warn)',
          }}
        >
          {props.projection.label}
        </span>
      )}

      <span
        aria-hidden="true"
        style={{
          position: 'relative',
          display: 'block',
          width,
          height: 6,
          background: 'var(--surface-3)',
          border: '1px solid var(--border)',
        }}
      >
        <span
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            // Full width, always — the fixed-px gradient below stays anchored to
            // the counter's absolute scale (colour at a given value never
            // shifts), and `clip-path` masks the unfilled remainder instead of
            // `width`, so the one value a player watches continuously animates
            // without triggering layout on every tick.
            width: '100%',
            clipPath: `inset(0 ${100 - fillPct}% 0 0)`,
            background: 'linear-gradient(90deg,var(--ok),var(--warn) 55%,var(--red))',
            backgroundSize: `${width}px 100%`,
            transition: 'clip-path .4s var(--ease)',
          }}
        />
        {projectedPct > 0 && (
          <span
            data-testid="escalation-projection-band"
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${fillPct}%`,
              width: `${projectedPct}%`,
              background:
                'repeating-linear-gradient(45deg,var(--warn) 0px,var(--warn) 4px,transparent 4px,transparent 8px)',
            }}
          />
        )}
        {ESCALATION_TICKS.map((tick) => (
          <span
            key={tick.level}
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${tick.pct}%`,
              width: 1,
              background: 'var(--border-strong)',
            }}
          />
        ))}
      </span>

      <span
        data-testid="escalation-band"
        style={{ ...LABEL, fontSize: 12, letterSpacing: '.1em', color: 'var(--text-2)' }}
      >
        {props.level}
      </span>
      {props.projection !== null && props.projection.bandHigh !== props.level && (
        <span
          data-testid="escalation-projected-band"
          style={{ ...LABEL, fontSize: 12, letterSpacing: '.1em', color: 'var(--warn)' }}
        >
          {props.projection.bandLow === props.projection.bandHigh
            ? `→ ${props.projection.bandHigh}`
            : `→ ${props.projection.bandLow}–${props.projection.bandHigh}`}
        </span>
      )}
    </div>
  );
}

const LABEL = {
  fontFamily: 'var(--font-ui)',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '.14em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
} as const;
